import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { ArrowRight, MapPin, Star, Users } from 'lucide-react';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReferralLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // ── Redirect logged-in users straight to homepage ───────────────
  // Covers: already-logged-in visitors, and users just registered via email
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!slug) return;
    // Persist slug so referral is tracked even if user navigates away before signing up
    localStorage.setItem('perkly_referral_slug', slug);
    localStorage.setItem('perkly_referral_ts', Date.now().toString());
    fetch(`${API}/r/${slug}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(d => { setBiz(d); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#F6F1E9] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-[#F6F1E9] flex items-center justify-center text-center px-4">
      <div>
        <p className="text-5xl mb-4">🔍</p>
        <h2 className="text-2xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Referral Link Not Found</h2>
        <Link to="/" className="btn-primary inline-flex">Go to Perkly</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <SEO
        title={biz ? `${biz.business_name} Deals in Athens, GA` : 'Local Deals in Athens, GA'}
        description={biz ? `Exclusive student deals at ${biz.business_name} in Athens, GA. ${biz.category ? biz.category + ' deals' : 'Claim your offer'} with Perkly.` : undefined}
        canonical={slug ? `/r/${slug}` : undefined}
      />
      {/* Perkly branding bar */}
      <div className="bg-[#2B2B2B] py-3 px-4 text-center">
        <span className="text-white/70 text-sm">Powered by </span>
        <Link to="/" className="text-[#C8A97E] font-bold">Perkly</Link>
        <span className="text-white/50 text-xs ml-2">— Athens, GA Local Deals Platform</span>
      </div>

      <div className="max-w-md mx-auto px-4 py-12 text-center">
        {/* Business logo / avatar */}
        <div className="mb-6">
          {biz.logo_url ? (
            <img src={biz.logo_url} alt={biz.business_name}
              className="w-20 h-20 rounded-full object-cover mx-auto border-4 border-white shadow-lg"
              onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <div className="w-20 h-20 rounded-full bg-[#C8A97E] flex items-center justify-center mx-auto shadow-lg">
              <span className="text-3xl font-bold text-white">{biz.business_name[0]}</span>
            </div>
          )}
        </div>

        {/* Headline */}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-2">Welcome from</p>
        <h1 className="text-4xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
          {biz.business_name}
        </h1>

        <div className="flex items-center justify-center gap-3 mb-2">
          <span className="text-sm text-[#2B2B2B]/60 flex items-center gap-1">
            <MapPin size={13} /> {biz.city_name}
          </span>
          <span className="text-sm text-[#2B2B2B]/60">{biz.category}</span>
        </div>

        <div className="flex items-center justify-center gap-1.5 mb-8">
          <span className="text-base">{biz.badge_emoji}</span>
          <span className="text-xs font-semibold text-[#C8A97E]">{biz.badge}</span>
          <span className="text-xs text-[#2B2B2B]/40">· {biz.total_members} members introduced</span>
        </div>

        {/* Value prop */}
        <div className="bg-white rounded-3xl p-7 shadow-sm border border-[#8A9A8B]/12 mb-7">
          <h2 className="text-2xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Unlock Exclusive Local Deals
          </h2>
          <p className="text-[#2B2B2B]/60 text-sm leading-relaxed mb-5">
            Discover the best food deals, nightlife, fitness discounts, and local experiences around Athens — all in one place. Free forever.
          </p>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { emoji: '🍔', label: 'Food Deals' },
              { emoji: '💪', label: 'Fitness' },
              { emoji: '🌙', label: 'Nightlife' },
            ].map(f => (
              <div key={f.label} className="bg-[#F6F1E9] rounded-2xl p-3 text-center">
                <div className="text-xl mb-1">{f.emoji}</div>
                <p className="text-xs font-semibold text-[#2B2B2B]/70">{f.label}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => setShowAuth(true)}
            data-testid="referral-signup-btn"
            className="w-full btn-primary justify-center py-4 text-base"
          >
            Create Free Account <ArrowRight size={16} />
          </button>
          <p className="text-xs text-[#2B2B2B]/40 mt-3">Free forever · No credit card needed</p>
        </div>

        {/* Trust signals */}
        <div className="flex items-center justify-center gap-2 text-xs text-[#2B2B2B]/50">
          <Star size={12} className="text-[#C8A97E]" />
          <span>Trusted by businesses and students across Athens, GA</span>
        </div>
      </div>

      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          returnTo="/"
          referredBySlug={slug}
        />
      )}
    </div>
  );
}
